import { Attribute } from '@/components/features/Search/Facets/useFacets';
import { Button, VStack } from '@chakra-ui/react';
import { PlusIcon } from '@sphere/icons';
import { AccordionItem } from '@sphere/ui';
import useTranslation from 'next-translate/useTranslation';
import { useRouter } from 'next/router';
import { rem } from 'polished';
import { useMemo, useState } from 'react';
import { MAX_FACET_ITEMS } from '../../config';
import AttributeAccordion from './AttributeAccordion';

type Props = {
  label: string;
  showCount?: boolean;
  attributes?: Attribute[];
};

export const AttributesFacet = ({ label, attributes, showCount }: Props) => {
  const { t } = useTranslation('search');
  const router = useRouter();
  const [maxItems, setMaxItems] = useState(MAX_FACET_ITEMS);

  const initialSelectedKeys = useMemo(
    () =>
      attributes
        ?.filter(attribute => attribute.values.some(value => value.isSelected))
        .map(attribute => attribute.key),
    /* We only want to set this variable on mount to keep the initial selected attribute keys persisted. */
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  const addAttributeFilter = (attributeKey: string, facetValue: string) => {
    const selectedAttributeValues = router?.query[`attributes[${attributeKey}]`] || [];

    router.replace(
      {
        query: {
          ...router.query,
          [`attributes[${attributeKey}]`]: [
            ...(Array.isArray(selectedAttributeValues)
              ? selectedAttributeValues
              : [selectedAttributeValues]),
            facetValue,
          ],
        },
      },
      undefined,
      { shallow: true },
    );
  };

  const addAttributeRangeFilter = (attributeKey: string, min: number, max: number) => {
    router.replace(
      {
        query: {
          ...router.query,
          [`attributes[${attributeKey}]`]: [min, max],
        },
      },
      undefined,
      { shallow: true },
    );
  };

  const removeAttributeFilter = (attributeKey: string, facetValue?: string) => {
    const selectedAttributeValues = router?.query[`attributes[${attributeKey}]`] || [];
    const { [`attributes[${attributeKey}]`]: _test, ...queryParams } = router.query;

    router.replace(
      {
        query: {
          ...queryParams,
          [`attributes[${attributeKey}]`]: facetValue
            ? Array.isArray(selectedAttributeValues)
              ? selectedAttributeValues.filter(value => value !== facetValue)
              : selectedAttributeValues === facetValue
              ? []
              : [selectedAttributeValues]
            : [],
        },
      },
      undefined,
      { shallow: true },
    );
  };

  const sortedAttributes = useMemo(
    () =>
      attributes
        ?.sort((a, b) => {
          const aIsSelected = initialSelectedKeys?.includes(a.key);
          const bIsSelected = initialSelectedKeys?.includes(b.key);

          return aIsSelected && !bIsSelected ? -1 : !aIsSelected && bIsSelected ? 1 : 0;
        })
        ?.slice(0, maxItems),
    [attributes, initialSelectedKeys, maxItems],
  );

  return (
    <AccordionItem
      isDefaultOpen={!!initialSelectedKeys?.length}
      label={label}
      isDisabled={!attributes?.length}
    >
      <VStack spacing="space.8">
        {sortedAttributes?.map(({ key, values, type }) => (
          <AttributeAccordion
            key={key}
            type={type}
            attribute={key}
            facetValues={values}
            addFilter={facetValue => addAttributeFilter(key, facetValue)}
            addRangeFilter={(min, max) => addAttributeRangeFilter(key, min, max)}
            removeFilter={facetValue => removeAttributeFilter(key, facetValue)}
            showCount={showCount}
          />
        ))}
      </VStack>

      {(attributes?.length || 0) > maxItems && (
        <Button
          variant="link"
          fontSize="sm"
          fontWeight="medium"
          onClick={() => setMaxItems(prevState => prevState + MAX_FACET_ITEMS)}
          minW="unset"
          leftIcon={<PlusIcon boxSize={rem(10)} />}
          iconSpacing="space.4"
          mt="space.12"
        >
          {t('facets.actions.show-more')}
        </Button>
      )}
    </AccordionItem>
  );
};
